import { zodResolver } from '@hookform/resolvers/zod';
import { ValidationErrorResponse } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
} from '@wyz/ui';
import { DateUtils, invariant } from '@wyz/utils';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { useManageBrands, useManageCriteria } from '~/common/api';
import {
  CriteriaBuilder,
  DateField,
  DropzoneAreaField,
  MatchedCustomersTable,
  PdfModal,
  SelectField,
  TextField,
} from '~/common/components';
import { Trans, useConfig, useI18n } from '~/common/context';
import { useBackTo, useValidationForm } from '~/common/hooks';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { CommonErrorCodesEnumType } from '~/common/types';
import { OdrOperationHelpers } from '~/connect/helpers';
import { odrOperationEditForm, odrOperationForm } from '~/connect/schemas';
import {
  IOdrOperationEditForm,
  IOdrOperationForm,
  IOdrOperationPayload,
  IOdrOperationUpdatePayload,
} from '~/connect/types';
import { useState } from 'react';

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const DEFAULT_VALUES: Partial<IOdrOperationEditForm> = {
  brands: [],
  criteria: [],
};

type Props =
  | {
      mode?: 'CREATE';
      defaultValues?: Partial<IOdrOperationForm>;
      isLoading: boolean;
      onSubmit: (data: IOdrOperationPayload) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    }
  | {
      mode: 'UPDATE';
      id: string;
      defaultValues: Partial<IOdrOperationEditForm>;
      existingFile: { fileName: string; filePath: string };
      isLoading: boolean;
      onSubmit: (data: IOdrOperationUpdatePayload) => void;
      errors?: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
    };

export function OdrOperationForm(props: Props) {
  const backTo = useBackTo('..');
  const { defaultValues = DEFAULT_VALUES, isLoading, errors } = props;
  const mode = props.mode ?? 'CREATE';
  const { t } = useI18n();
  const { apiUrl, tenantCode } = useConfig();
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false);

  // The edit schema only differs from the create one in making `file` optional
  // (replacing the visual is opt-in on edit) — reused as one RHF value type for both.
  const methods = useForm<IOdrOperationEditForm>({
    defaultValues,
    resolver: zodResolver(
      mode === 'UPDATE' ? odrOperationEditForm : odrOperationForm,
    ),
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const manageCriteria = useManageCriteria({ getAll: { enabled: true } });
  const manageBrands = useManageBrands({
    paginate: {
      enabled: true,
      args: {
        featureContext: FEATURE_CONTEXT.ODR_OPERATION,
      },
    },
  });

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => {
        invariant(manageCriteria.getAll.isSuccess);
        if (props.mode === 'UPDATE') {
          props.onSubmit(
            OdrOperationHelpers.fromEditFormToPayload(
              data,
              manageCriteria.getAll.data.data,
            ),
          );
        } else {
          // The create schema requires `file`, so it is always set here.
          props.onSubmit(
            OdrOperationHelpers.fromFormToPayload(
              data as IOdrOperationForm,
              manageCriteria.getAll.data.data,
            ),
          );
        }
      }}
    >
      <Card className='mb-section'>
        <Card.Header>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='post' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.general_information' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <TextField
                name='name'
                label={<Trans i18nKey='app.odr_operation.form.name' />}
                required
              />
            </Col>
            <Col md={6}>
              <SelectField<true>
                name='brands'
                label={<Trans i18nKey='app.odr_operation.form.brands' />}
                options={
                  manageBrands.paginate.data?.data.map((brand) => ({
                    label: brand.name,
                    value: brand.publicKey,
                  })) || []
                }
                isMulti={true}
                maxShown={Infinity}
                wrapItems
                required
                isLoading={manageBrands.paginate.isLoading}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <DateField
                name='startDate'
                label={<Trans i18nKey='app.common.start_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
            <Col md={6}>
              <DateField
                name='endDate'
                label={<Trans i18nKey='app.common.end_date' />}
                placeholderText='dd-MM-yyyy'
                dateFormat={DateUtils.DATE_FORMAT.DMY}
                required
                showTimeSelect={false}
                translations={{
                  applyButton: t('app.common.button.apply'),
                  resetButton: t('app.common.button.reset'),
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.common.criteria' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          <CriteriaBuilder name={'criteria'}>
            <Row className='mt-4'>
              <Col md={6}>
                <MatchedCustomersTable
                  featureContext={FEATURE_CONTEXT.ODR_OPERATION}
                  name={'criteria'}
                  matchUserContext
                />
              </Col>
            </Row>
          </CriteriaBuilder>
        </Card.Body>
      </Card>

      <Card className='mb-section'>
        <Card.Header className='pb-0'>
          <Title heading='lg' color='tertiary' weight='bold'>
            <Icon icon='download' width='1.5rem' height='1.5rem' />
            <span className='ms-2'>
              <Trans i18nKey='app.odr_operation.form.file.title' />
            </span>
          </Title>
        </Card.Header>
        <Card.Body>
          {props.mode === 'UPDATE' && (
            <Text color='secondary-gray' className='d-block mb-2'>
              <Trans i18nKey='app.odr_operation.form.file.current' />{' '}
              <a
                className='cursor-pointer'
                onClick={() => setShowPdfModal(true)}
                data-cy='odr-operation-existing-file-link'
              >
                {props.existingFile.fileName}
              </a>
              {showPdfModal && (
                <PdfModal
                  filename={props.existingFile.fileName}
                  onClose={() => setShowPdfModal(false)}
                  url={`${apiUrl}/tenants/api/wtb/tenants/${tenantCode}/odr-operations/${props.id}/visual`}
                />
              )}
            </Text>
          )}
          <DropzoneAreaField
            name='file'
            label={<Trans i18nKey='app.odr_operation.form.file' />}
            filesLimit={1}
            maxFileSize={MAX_FILE_SIZE}
            acceptedFiles='PDF'
            required={mode !== 'UPDATE'}
          />
        </Card.Body>
      </Card>
      <Card className='mb-section'>
        <Card.Body>
          <Flex className='space-x-4' justifyContent='between'>
            <Button
              id='cancel'
              as={Link}
              variant='secondary'
              type='button'
              to={backTo}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id='submit'
              variant='primary'
              type='submit'
              disabled={isLoading}
            >
              <Trans
                i18nKey={
                  mode === 'UPDATE'
                    ? 'app.common.buttons.update'
                    : 'app.odr_operation.form.buttons.create'
                }
              />
            </Button>
          </Flex>
        </Card.Body>
      </Card>
    </FormContainer>
  );
}
